import { getMsgDeskRuntime } from '@/msgdeskRuntime/runtime/MsgDeskRuntime'
import type { LegacyMoveDestination } from '@/msgdeskRuntime/contracts/itemMovements'
import type { LegacyHandlerName } from '@/msgdeskRuntime/contracts/legacyEvents'
import { enqueuePreBootAction, isLegacyCommandLive } from './preBootQueue'

// Shared helpers for the parallel (v2) template tree.
// These do NOT touch existing code; they only read the legacy command facade
// and parse inline style strings copied from the legacy markup.

export function cmd(): any {
  return getMsgDeskRuntime().facade.appCommand()
}

// S1 pre-boot window (docs/27 §4 item 3): the desk is server-rendered and
// interactive before the legacy runtime chunk boots. Any command dispatched
// while the command facade is missing or the bootstrap has not completed is
// queued and replayed in order once the app host marks the runtime live.
function runOrQueueLegacyCommand(name: string, run: () => unknown): unknown {
  if (isLegacyCommandLive() && cmd()) return run()
  enqueuePreBootAction(name, run)
  return undefined
}

// The name union is generated from the real legacy dispatch tables
// (scripts/check-legacy-events.mjs); dispatching an unknown name is a compile
// error instead of a silent runtime no-op.
export function legacyEvent(name: LegacyHandlerName, ...args: unknown[]): unknown {
  return runOrQueueLegacyCommand(name, () => cmd()?.legacyEvent(name, ...args))
}

function isVirtualizableLegacyElement(el: HTMLElement): boolean {
  return Boolean(
    el.hasAttribute('data-md-contract') ||
    el.hasAttribute('data-md-el-id') ||
    el.closest('[data-md-contract]') ||
    el.closest('[data-md-el-id]'),
  )
}

function assertLegacyTarget(target: unknown, action: string): unknown {
  if (typeof HTMLElement === 'undefined' || !(target instanceof HTMLElement)) return target
  if (isVirtualizableLegacyElement(target)) return target

  throw new Error(`[legacy action] Unvirtualized target for ${action}`)
}

export function hoverMenuItem(el: unknown, isOver: boolean, isParents?: boolean): unknown {
  const target = assertLegacyTarget(el, 'hoverMenuItem')
  return legacyEvent('mev_msg_menu_li_over_evm', target, isOver, isParents)
}

export function switchMenu(el: unknown, switcherId: number): unknown {
  const target = assertLegacyTarget(el, 'switchMenu')
  return legacyEvent('mev_msg_menu_switcher_evm', target, switcherId)
}

export function switchToBrand(el: unknown): unknown {
  return switchMenu(el, 500)
}

export function backToMainMessage(el: unknown): unknown {
  return switchMenu(el, 0)
}

export function openReplyEditor(): unknown {
  return legacyEvent('mev_msg_show_new_msg_evm', false, false)
}

export function openEditEditor(): unknown {
  return legacyEvent('mev_msg_show_new_msg_evm', true, true)
}

export function openNewMessageEditor(): unknown {
  return legacyEvent('mev_msg_show_new_msg_evm', false, true)
}

export function toggleEditButtonGroup(el: unknown): unknown {
  const target = assertLegacyTarget(el, 'toggleEditButtonGroup')
  return legacyEvent('mev_msg_toggle_edit_buttons_evm', target)
}

export function saveEditor(el: unknown, action: 'save_msg' | 'save_draft' = 'save_msg'): unknown {
  const target = assertLegacyTarget(el, 'saveEditor')
  return legacyEvent('save_msg', target, action)
}

export function saveEditorMessage(el: unknown): unknown {
  return saveEditor(el, 'save_msg')
}

export function saveEditorDraft(el: unknown): unknown {
  return saveEditor(el, 'save_draft')
}

// pin_type 0 = show/view pin, 1 = edit pin (legacy m30 protect ordering).
// Runs legacy is_to_protect: enables/disables the pin input, restores the
// stored value or the "No password" label, and revalidates the protect form.
export function toggleEditorPinProtection(el: unknown, pinType: 0 | 1): unknown {
  const target = assertLegacyTarget(el, 'toggleEditorPinProtection')
  return legacyEvent('html_is_to_protect', target, pinType)
}

export function toggleEditorMoreSettings(el: unknown, tabId?: number): unknown {
  const target = assertLegacyTarget(el, 'toggleEditorMoreSettings')
  return tabId === undefined
    ? legacyEvent('html_toggle_more_settings', target)
    : legacyEvent('html_toggle_more_settings', target, tabId)
}

export function deriveEditorSubKey(el: unknown): unknown {
  const target = assertLegacyTarget(el, 'deriveEditorSubKey')
  return legacyEvent('sub_key', target)
}

export function checkEditorNameKey(el: unknown): unknown {
  const target = assertLegacyTarget(el, 'checkEditorNameKey')
  return legacyEvent('check_name_key', target)
}

export function toggleEditorCodeKey(isOpen: boolean): unknown {
  return legacyEvent('html_toggle_code_key', isOpen)
}

export function checkEditorCodeKey(el: unknown): unknown {
  const target = assertLegacyTarget(el, 'checkEditorCodeKey')
  return legacyEvent('check_code_key', target)
}

export function toggleRichEditor(el: unknown): unknown {
  const target = assertLegacyTarget(el, 'toggleRichEditor')
  return legacyEvent('html_toggle_rich_edit', target)
}

export function toggleRichEditorBar(el: unknown, barId: number): unknown {
  const target = assertLegacyTarget(el, 'toggleRichEditorBar')
  return legacyEvent('html_toggle_bars', target, barId)
}

export function loadMoreSubs(el: unknown, parentElId: number, section: 0 | 1): unknown {
  const target = assertLegacyTarget(el, 'loadMoreSubs')
  return legacyEvent('mev_msg_get_more_subs_evm', target, parentElId, section)
}

export function refreshSubs(el: unknown, section: 0 | 1): unknown {
  const target = assertLegacyTarget(el, 'refreshSubs')
  return legacyEvent('mev_msg_refresh_subs_evm', target, 0, section)
}

export function setSubsPage(el: unknown, section: 0 | 1, page: number | 'prev' | 'next'): unknown {
  const target = assertLegacyTarget(el, 'setSubsPage')
  return legacyEvent('mev_msg_subs_set_page_evm', target, section, page)
}

export function toggleMainMessageFold(): unknown {
  return legacyEvent('mev_msg_toggle_msg_evm')
}

export function toggleSubsFold(el: unknown, parentElId: number, section: 0 | 1 | -1): unknown {
  const target = assertLegacyTarget(el, 'toggleSubsFold')
  return legacyEvent('mev_msg_toggle_subs_evm', target, parentElId, section)
}

export function toggleMenuModeSection(el: unknown, mode: string, section: 0 | 1): unknown {
  const target = assertLegacyTarget(el, 'toggleMenuModeSection')
  return legacyEvent('mev_msg_toggle_menu_subs_evm', target, mode, section)
}

export function toggleFavorite(el: unknown, elId: number, force = false, sourceMode?: 'favs'): unknown {
  const target = assertLegacyTarget(el, 'toggleFavorite')
  // `force` lets a logged visitor toggle on an edit-locked desk (FAVS-VISITOR):
  // the legacy _toggle_fav edit-lock guard would otherwise drop the request.
  return legacyEvent('mev_msg_toggle_fav_evm', target, elId, force, sourceMode)
}

export function toggleEmailButton(el: unknown, isMouse?: boolean, isOver?: boolean): unknown {
  const target = assertLegacyTarget(el, 'toggleEmailButton')
  if (isMouse === undefined) return legacyEvent('mev_msg_email_button_evm', target)
  if (isOver === undefined) return legacyEvent('mev_msg_email_button_evm', target, isMouse)
  return legacyEvent('mev_msg_email_button_evm', target, isMouse, isOver)
}

export function toggleMainSiteView(): unknown {
  // TODO(product): the legacy dispatch table has NEVER contained
  // 'mev_msg_mainmsg_siteview_evm' — the site-view button has always been a
  // silent runtime no-op. Kept as an explicit no-op so today's behavior is
  // unchanged; wire a real legacy handler here once the intended behavior is
  // decided.
  return undefined
}

export function saveQuickReply(): unknown {
  return legacyEvent('mev_msg_save_qr_evm')
}

export function checkSub(el: unknown, elId: number, checked: boolean): unknown {
  const target = assertLegacyTarget(el, 'checkSub')
  return runOrQueueLegacyCommand('checkSub', () => {
    const command = cmd()
    return command?.checkSub
      ? command.checkSub(target, elId, checked)
      : command?.legacyEvent('mev_msg_check_sub_evm', target, elId, checked)
  })
}

export function checkVisibleSubs(el: unknown, section: 0 | 1): unknown {
  const target = assertLegacyTarget(el, 'checkVisibleSubs')
  return runOrQueueLegacyCommand('checkVisibleSubs', () => {
    const command = cmd()
    return command?.checkVisibleSubs
      ? command.checkVisibleSubs(target, section)
      : command?.legacyEvent('mev_msg_check_subs_evm', target, 0, section)
  })
}

export function moveCheckedTo(
  el: unknown,
  section: 0 | 1,
  toMode: LegacyMoveDestination,
  options: { mode?: string; reason?: string } = {},
): unknown {
  const target = assertLegacyTarget(el, 'moveCheckedTo')
  return runOrQueueLegacyCommand('moveCheckedTo', () => {
    const command = cmd()
    return command?.moveCheckedTo
      ? command.moveCheckedTo(target, section, toMode, { section, ...options })
      : command?.legacyEvent('mev_msg_move_subs_evm', target, section, toMode)
  })
}

export function moveCheckedToTrash(el: unknown, section: 0 | 1): unknown {
  const command = cmd()
  return command?.moveCheckedToTrash
    ? command.moveCheckedToTrash(el, { section })
    : moveCheckedTo(el, section, 'trash')
}

export function moveCheckedToDrafts(el: unknown, section: 0 | 1): unknown {
  const command = cmd()
  return command?.moveCheckedToDrafts
    ? command.moveCheckedToDrafts(el, { section })
    : moveCheckedTo(el, section, 'drafts')
}

export function restoreChecked(el: unknown, section: 0 | 1): unknown {
  const command = cmd()
  return command?.restoreChecked
    ? command.restoreChecked(el, { section })
    : moveCheckedTo(el, section, 'subs')
}

function getCheckedIds(section: 0 | 1, mode: string): number[] {
  return getMsgDeskRuntime().facade.checkedIds(section, mode) as number[]
}

function getSubRenderState(elId: number): any {
  return getMsgDeskRuntime().facade.subRenderState(elId)
}

export async function setCheckedFavoriteState(
  el: unknown,
  section: 0 | 1,
  mode: string,
  isFavorite: boolean,
): Promise<number> {
  const msgObj = getMsgDeskRuntime().facade.appMsgObj
  const ids = getCheckedIds(section, mode).filter((elId) => {
    const sub = getSubRenderState(elId)
    return sub ? !!sub.flags?.isFavorite !== isFavorite : true
  })

  for (const elId of ids) {
    toggleFavorite(el, elId)
    await msgObj?.legacy?.whenIdle?.(`setCheckedFavoriteState:${mode}:${section}:${elId}`)
  }

  return ids.length
}

export type LegacyInlineStyle = Record<string, string | number>

export function styleFromString(style: string): LegacyInlineStyle {
  const out: Record<string, string> = {}
  for (const part of style.split(';')) {
    const index = part.indexOf(':')
    if (index < 0) continue
    const rawName = part.slice(0, index).trim()
    const value = part.slice(index + 1).trim()
    if (!rawName || !value) continue
    const name = rawName.replace(/-([a-z])/g, (_, char: string) => char.toUpperCase())
    out[name] = value
  }
  return out
}
